import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import TimerOffIcon from '@mui/icons-material/TimerOff';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { GetLayoutChartDataResponse } from 'components/ChartContainer/types';
import { translateBlock } from 'i18n';
import TimeFilterModal from './TimeFilterModal';
import TimeFilterTooltip from './TimeFilterTooltip';

interface TimeFilterTooltipProps {
    chartKey: number;
    timeFilters: GetLayoutChartDataResponse['timeFilters'] | null;
}

const StyContainer = styled(Box, {
    name: 'StyContainer',
})({
    pointerEvents: 'none',
});

const StyTimeFilterCard = styled(Card, {
    name: 'StyTimeFilterCard',
    shouldForwardProp: (prop) => prop !== 'hasTimeFilter',
})<{ hasTimeFilter: boolean }>(({ hasTimeFilter, theme }) => ({
    position: 'relative',
    maxWidth: '75%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomLeftRadius: '0%',
    borderBottomRightRadius: '0%',
    backgroundColor: hasTimeFilter
        ? theme.palette.primary.background
        : theme.palette.lightRed,
}));

const StyTimeFilterContent = styled('div', {
    name: 'StyTimeFilterContent',
})({
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: '0.5rem !important',
    pointerEvents: 'auto',
});

/* Returns the string representation of a chart's time filters list. */
function getTimeFilterMessage(timeFilters: string[]): string {
    // if there is no time filter
    const firstTimeFilter = timeFilters[0];
    return timeFilters.length === 1
        ? firstTimeFilter
        : `${firstTimeFilter}, +${timeFilters.length - 1}`;
}

const TimeFilterCard = ({ chartKey, timeFilters }: TimeFilterTooltipProps) => {
    const { t } = useTranslation();
    const [showModal, setShowModal] = React.useState(false);
    if (!timeFilters) {
        return <></>;
    }

    // Convert the list of metrics to a string and a tooltip title
    const hasTimeFilter = timeFilters.length > 0;
    const timeFiltersFields = Array.from(
        new Set(timeFilters.map((filter) => translateBlock(filter.field))),
    );
    return (
        <StyContainer className="grid-chart-bottom">
            <StyTimeFilterCard
                className="chart-bottom-time-filter"
                hasTimeFilter={hasTimeFilter}
                onClick={() => setShowModal(true)}
            >
                <CardActionArea>
                    <TimeFilterTooltip
                        chartKey={chartKey}
                        timeFiltersFields={timeFiltersFields}
                    >
                        <StyTimeFilterContent>
                            {!hasTimeFilter ? (
                                <TimerOffIcon
                                    sx={{ color: 'red' }}
                                    fontSize="small"
                                />
                            ) : (
                                <Typography
                                    variant="subtitle2"
                                    align="center"
                                    sx={{ color: 'primary.main' }}
                                >
                                    {`${t(
                                        'timeFilterTooltip.filterOn',
                                    )}: ${getTimeFilterMessage(
                                        timeFiltersFields,
                                    )}`}
                                </Typography>
                            )}
                        </StyTimeFilterContent>
                    </TimeFilterTooltip>
                </CardActionArea>
            </StyTimeFilterCard>
            {showModal && (
                <TimeFilterModal
                    closeModal={() => setShowModal(false)}
                    timeFilters={timeFilters}
                />
            )}
        </StyContainer>
    );
};

export default TimeFilterCard;
